// src/webideal-jobs/pages/Settings.jsx
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { 
  User, 
  Lock, 
  Shield, 
  Bell, 
  FileText, 
  Trash2, 
  Save, 
  CheckCircle2, 
  AlertCircle,
  ChevronRight,
  LogOut,
  Mail,
  Phone,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";

export default function Settings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeSection, setActiveSection] = useState("account");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);


  // Fetch Settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ["user-settings"],
    queryFn: () => backendApi.Settings.getAll(),
    retry: false,
    onError: (err) => {
      if (error.status === 401) {
        logout();
        navigate("/jobs/auth");
      }
    }
  });

  // Mutations
  const updateAccountMutation = useMutation({
    mutationFn: (data) => backendApi.Settings.updateAccount(data),
    onSuccess: () => {
      setMessage({ type: "success", text: "Account settings updated successfully!" });
      queryClient.invalidateQueries(["user-settings"]);
    },
    onError: (err) => setMessage({ type: "error", text: err.message || "Failed to update account" })
  });

  const updatePrivacyMutation = useMutation({
    mutationFn: (data) => backendApi.Settings.updatePrivacy(data),
    onSuccess: () => setMessage({ type: "success", text: "Privacy settings saved!" }),
    onError: (err) => setMessage({ type: "error", text: "Error saving privacy settings" })
  });

  const updateNotificationsMutation = useMutation({
    mutationFn: (data) => backendApi.Settings.updateNotifications(data),
    onSuccess: () => setMessage({ type: "success", text: "Notification preferences updated!" }),
    onError: (err) => setMessage({ type: "error", text: "Error updating notifications" })
  });

  const updateResumeMutation = useMutation({
    mutationFn: (data) => backendApi.Settings.updateResume(data),
    onSuccess: () => setMessage({ type: "success", text: "Resume sharing settings updated!" }),
    onError: (err) => setMessage({ type: "error", text: "Error saving resume settings" })
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data) => backendApi.Settings.changePassword(data),
    onSuccess: () => {
      setMessage({ type: "success", text: "Password changed successfully!" });
      setPasswordForm({ old: "", new: "", confirm: "" });
    },
    onError: (err) => setMessage({ type: "error", text: err.message || "Password change failed" })
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => backendApi.Settings.deleteAccount(),
    onSuccess: () => {
      logout();
      navigate("/jobs/auth");
    }
  });

  const logoutFromAllMutation = useMutation({
    mutationFn: () => backendApi.Settings.logoutFromAll(),
    onSuccess: () => {
      setMessage({ type: "success", text: "Logged out from all devices successfully!" });
      setTimeout(() => {
        logout();
        navigate("/jobs/auth");
      }, 2000);
    },
    onError: () => setMessage({ type: "error", text: "Failed to logout from all devices" })
  });

  // Local States for Forms
  const [accountForm, setAccountForm] = useState({ full_name: "", email: "", phone: "" });
  const [passwordForm, setPasswordForm] = useState({ old: "", new: "", confirm: "" });
  const [privacyForm, setPrivacyForm] = useState({ profile_visibility: "public", show_email: true, show_phone: true });
  const [notificationsForm, setNotificationsForm] = useState({ email_notifications: true, job_alerts: true, application_updates: true });
  const [resumeForm, setResumeForm] = useState({ auto_share_resume: false, allow_recruiter_download: true });

  useEffect(() => {
    if (settings) {
      setAccountForm({
        full_name: settings.account.full_name || "",
        email: settings.account.email || "",
        phone: settings.account.phone || ""
      });
      setPrivacyForm(settings.privacy);
      setNotificationsForm(settings.notifications);
      setResumeForm(settings.resume);
    }
  }, [settings]);

  const navItems = [
    { id: "account", label: "Account Details", icon: User },
    { id: "security", label: "Login & Security", icon: Shield },
    { id: "privacy", label: "Privacy Control", icon: Lock },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "resume", label: "Resume Sharing", icon: FileText },
    { id: "danger", label: "Danger Zone", icon: Trash2, danger: true },
  ];

  if (isLoading) return <div className="flex items-center justify-center min-h-[400px]">Loading Settings...</div>;
  if (error) return (
    <div className="text-center py-20 animate-in fade-in">
      <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center text-rose-600 mx-auto mb-4">
        <AlertCircle className="w-8 h-8" />
      </div>
      <h2 className="text-xl font-bold text-slate-800">Error Loading Settings</h2>
      <p className="text-slate-500 mt-2 max-w-sm mx-auto">{error.message || "Please check your connection and try again later."}</p>
    </div>
  );

  const SectionHeader = ({ title, description }) => (
    <div className="mb-6">
      <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
      <p className="text-slate-500 text-sm mt-1">{description}</p>
    </div>
  );

  const FormField = ({ label, children }) => (
    <div className="space-y-2">
      <label className="text-sm font-semibold text-slate-600 block">{label}</label>
      {children}
    </div>
  );

  const ToggleItem = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-all">
      <div className="space-y-1">
        <p className="font-semibold text-slate-800 text-sm">{label}</p>
        <p className="text-xs text-slate-500 max-w-[400px]">{description}</p>
      </div>
      <div 
        onClick={onChange}
        className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors duration-200 ${checked ? 'bg-blue-600' : 'bg-slate-300'}`}
      >
        <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-0'}`} />
      </div>
    </div>
  );

  const handleSaveAccount = (e) => {
    e.preventDefault();
    updateAccountMutation.mutate(accountForm);
  };

  const handleSavePassword = (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }
    changePasswordMutation.mutate({ oldPassword: passwordForm.old, newPassword: passwordForm.new });
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto py-10 px-4 font-sans">
      {/* Sidebar Nav */}
      <div className="w-full lg:w-72 space-y-2">
        <div className="bg-white rounded-3xl border border-slate-200 p-2 shadow-sm sticky top-24">
          <div className="px-4 py-6 text-center border-b border-slate-50 mb-4">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-3xl font-bold text-blue-700 mx-auto shadow-inner border-4 border-white">
              {accountForm.full_name?.charAt(0)?.toUpperCase()}
            </div>
            <h3 className="mt-4 font-bold text-slate-900 truncate">{accountForm.full_name}</h3>
            <p className="text-xs text-slate-400 truncate">{accountForm.email}</p>
          </div>
          
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  setMessage({ type: "", text: "" });
                }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-300 group ${
                  isActive 
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' 
                    : item.danger 
                      ? 'text-rose-500 hover:bg-rose-50' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : ''}`} />
                  <span className="font-semibold text-sm">{item.label}</span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${isActive ? 'rotate-90' : 'group-hover:translate-x-1'}`} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 space-y-6 min-h-[600px] animate-in fade-in slide-in-from-right-4 duration-500">
        {message.text && (
          <div className={`p-4 rounded-2xl flex items-center gap-3 border shadow-sm ${
            message.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-rose-50 border-rose-100 text-rose-700"
          }`}>
            {message.type === "success" ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="text-sm font-semibold">{message.text}</span>
          </div>
        )}

        {/* 1. Account Settings */}
        {activeSection === "account" && (
          <Card className="border-slate-200 rounded-3xl shadow-sm overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <SectionHeader 
                title="Account Settings" 
                description="Manage your registration details and contact information." 
              />
            </CardHeader>
            <CardContent className="pt-8 space-y-6">
              <form onSubmit={handleSaveAccount} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Full Name">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                      type="text" 
                      value={accountForm.full_name} 
                      onChange={(e) => setAccountForm({ ...accountForm, full_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    />
                  </div>
                </FormField>

                <FormField label="Email Address">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                      type="email" 
                      value={accountForm.email} 
                      onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                      className="w-full pl-10 pr-24 py-3 bg-slate-100 border border-slate-200 rounded-xl outline-none"
                    />
                    {settings?.account?.is_verified && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-lg uppercase tracking-wider">
                        Verified
                      </span>
                    )}
                  </div>
                </FormField>

                <FormField label="Phone Number">
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                      type="tel" 
                      value={accountForm.phone} 
                      onChange={(e) => setAccountForm({ ...accountForm, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    />
                  </div>
                </FormField>

                <div className="md:col-span-2 pt-4">
                  <Button 
                    type="submit" 
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-2xl h-12 px-8 font-bold shadow-lg shadow-blue-100"
                    disabled={updateAccountMutation.isLoading}
                  >
                    {updateAccountMutation.isLoading ? "Saving..." : <><Save className="w-4 h-4 mr-2" /> Save Account Details</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* 2. Password & Security */}
        {activeSection === "security" && (
          <Card className="border-slate-200 rounded-3xl shadow-sm overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <SectionHeader title="Password & Security" description="Keep your account secure by changing your password regularly." />
            </CardHeader>
            <CardContent className="pt-8 space-y-8">
              <form onSubmit={handleSavePassword} className="max-w-md space-y-6">
                <FormField label="Current Password">
                  <input 
                    type="password" 
                    value={passwordForm.old} 
                    onChange={(e) => setPasswordForm({ ...passwordForm, old: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                </FormField>
                <FormField label="New Password">
                  <input 
                    type="password" 
                    value={passwordForm.new} 
                    onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                </FormField>
                <FormField label="Confirm New Password">
                  <input 
                    type="password" 
                    value={passwordForm.confirm} 
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                </FormField>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-2xl h-12 w-full font-bold shadow-lg shadow-blue-100"
                  disabled={changePasswordMutation.isLoading}
                >
                  {changePasswordMutation.isLoading ? "Updating..." : "Update Password"}
                </Button>
              </form>

              <div className="pt-8 border-t border-slate-100">
                <h4 className="font-bold text-slate-800 mb-2">Device Management</h4>
                <p className="text-sm text-slate-500 mb-4">You are currently logged in on this device. If you notice suspicious activity, log out from all sessions.</p>
                <Button 
                  variant="outline" 
                  className="border-slate-200 text-slate-600 rounded-2xl h-11 px-6 hover:bg-slate-50 font-bold"
                  onClick={() => logoutFromAllMutation.mutate()}
                  disabled={logoutFromAllMutation.isLoading}
                >
                  <LogOut className="w-4 h-4 mr-2" /> 
                  {logoutFromAllMutation.isLoading ? "Logging out..." : "Logout from all devices"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3. Privacy Settings */}
        {activeSection === "privacy" && (
          <Card className="border-slate-200 rounded-3xl shadow-sm overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <SectionHeader title="Privacy Settings" description="Control who can see your profile and contact information." />
            </CardHeader>
            <CardContent className="pt-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  onClick={() => setPrivacyForm({ ...privacyForm, profile_visibility: 'public' })}
                  className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${privacyForm.profile_visibility === 'public' ? 'border-blue-600 bg-blue-50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                >
                  <Eye className={`w-8 h-8 mb-3 ${privacyForm.profile_visibility === 'public' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <h4 className="font-bold text-slate-800">Public Profile</h4>
                  <p className="text-xs text-slate-500 mt-1">Visible to all registered recruiters and hiring managers.</p>
                </div>
                <div 
                  onClick={() => setPrivacyForm({ ...privacyForm, profile_visibility: 'private' })}
                  className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${privacyForm.profile_visibility === 'private' ? 'border-blue-600 bg-blue-50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                >
                  <EyeOff className={`w-8 h-8 mb-3 ${privacyForm.profile_visibility === 'private' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <h4 className="font-bold text-slate-800">Private Profile</h4>
                  <p className="text-xs text-slate-500 mt-1">Only companies you apply to will be able to view your profile.</p>
                </div>
              </div>

              <div className="space-y-4 pt-4">
                <ToggleItem 
                  label="Show email to recruiters" 
                  description="When enabled, employers can see your email address directly on your profile." 
                  checked={privacyForm.show_email}
                  onChange={() => setPrivacyForm({ ...privacyForm, show_email: !privacyForm.show_email })}
                />
                <ToggleItem 
                  label="Show phone number" 
                  description="Allow recruiters to call you by showing your mobile number on your profile." 
                  checked={privacyForm.show_phone}
                  onChange={() => setPrivacyForm({ ...privacyForm, show_phone: !privacyForm.show_phone })}
                />
              </div>

              <div className="pt-4">
                <Button 
                  onClick={() => updatePrivacyMutation.mutate(privacyForm)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-2xl h-11 px-8 font-bold"
                  disabled={updatePrivacyMutation.isLoading}
                >
                  {updatePrivacyMutation.isLoading ? "Saving..." : "Save Privacy Settings"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 4. Notification Settings */}
        {activeSection === "notifications" && (
          <Card className="border-slate-200 rounded-3xl shadow-sm overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <SectionHeader title="Notification Settings" description="Choose how often and when you want to be notified." />
            </CardHeader>
            <CardContent className="pt-8 space-y-4">
              <ToggleItem 
                label="Email Notifications" 
                description="General account activities, security alerts, and weekly digests." 
                checked={notificationsForm.email_notifications}
                onChange={() => setNotificationsForm({ ...notificationsForm, email_notifications: !notificationsForm.email_notifications })}
              />
              <ToggleItem 
                label="Job Alerts" 
                description="Personalized job recommendations based on your profile and search history." 
                checked={notificationsForm.job_alerts}
                onChange={() => setNotificationsForm({ ...notificationsForm, job_alerts: !notificationsForm.job_alerts })}
              />
              <ToggleItem 
                label="Application Updates" 
                description="Status changes, messages from recruiters, and interview invitations." 
                checked={notificationsForm.application_updates}
                onChange={() => setNotificationsForm({ ...notificationsForm, application_updates: !notificationsForm.application_updates })}
              />
              <div className="pt-6">
                <Button 
                  onClick={() => updateNotificationsMutation.mutate(notificationsForm)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-2xl h-11 px-8 font-bold"
                  disabled={updateNotificationsMutation.isLoading}
                >
                  {updateNotificationsMutation.isLoading ? "Saving..." : "Save Preferences"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 5. Resume Settings */}
        {activeSection === "resume" && (
          <Card className="border-slate-200 rounded-3xl shadow-sm overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <SectionHeader title="Resume sharing & Download" description="Control how your resume is shared with potential employers." />
            </CardHeader>
            <CardContent className="pt-8 space-y-4">
              <ToggleItem 
                label="Auto share resume" 
                description="Automatically include your resume when applying to relevant roles." 
                checked={resumeForm.auto_share_resume}
                onChange={() => setResumeForm({ ...resumeForm, auto_share_resume: !resumeForm.auto_share_resume })}
              />
              <ToggleItem 
                label="Allow recruiters to download resume" 
                description="Enables recruiters who find you via search to save a copy of your resume." 
                checked={resumeForm.allow_recruiter_download}
                onChange={() => setResumeForm({ ...resumeForm, allow_recruiter_download: !resumeForm.allow_recruiter_download })}
              />
              <div className="pt-6">
                <Button 
                  onClick={() => updateResumeMutation.mutate(resumeForm)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-2xl h-11 px-8 font-bold"
                  disabled={updateResumeMutation.isLoading}
                >
                  {updateResumeMutation.isLoading ? "Saving..." : "Save Resume Preferences"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 6. Danger Zone */}
        {activeSection === "danger" && (
          <Card className="border-rose-200 bg-rose-50/20 rounded-3xl shadow-none">
            <CardHeader>
              <h2 className="text-2xl font-bold text-rose-600 flex items-center gap-2">
                <AlertCircle className="w-7 h-7" /> Danger Zone
              </h2>
              <p className="text-slate-500 text-sm mt-1">Actions here are permanent and cannot be undone.</p>
            </CardHeader>
            <CardContent className="pt-4 space-y-6">
              <div className="p-6 bg-white border border-rose-100 rounded-2xl">
                <h4 className="font-bold text-slate-800">Delete Account</h4>
                <p className="text-sm text-slate-500 mt-2">
                  Once your account is deleted, all your profile information, job applications, 
                  and saved data will be permanently removed. There is no way to recover it.
                </p>
                <Button 
                  onClick={() => setShowDeleteModal(true)}
                  className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl mt-6 font-bold h-11 px-8"
                >
                  Delete My Account Forever
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 mx-auto mb-6">
              <AlertCircle className="w-10 h-10" />
            </div>
            <h3 className="text-2xl font-bold text-center text-slate-900">Are you absolutely sure?</h3>
            <p className="text-center text-slate-500 mt-3 text-sm leading-relaxed">
              This action will permanently delete your WebIdeal profile and all associated data. This cannot be undone.
            </p>
            <div className="flex flex-col gap-3 mt-8">
              <Button 
                onClick={() => deleteAccountMutation.mutate()}
                className="bg-rose-600 hover:bg-rose-700 text-white rounded-2xl h-12 font-bold shadow-lg shadow-rose-100"
                disabled={deleteAccountMutation.isLoading}
              >
                {deleteAccountMutation.isLoading ? "Deleting..." : "Yes, Delete Everything"}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setShowDeleteModal(false)}
                className="text-slate-500 font-bold h-12 hover:bg-slate-50 rounded-2xl"
              >
                No, Keep My Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
